/**
 * Finance categorization rules — management screen.
 *
 * Rules can already be CREATED inline from the "Always do this" dialog after a
 * manual categorization (see RuleFromCategorizationDialog). This screen is the
 * place to SEE, EDIT, REORDER (via priority), toggle, and DELETE them — the CRUD
 * surface that was missing from the UI even though the API supports it fully.
 *
 * Rules are deterministic (ADR 0020): ordered by (priority ASC, created ASC),
 * first match wins, and a rule is skipped when its category kind contradicts the
 * transaction sign. "Run now" re-applies every active rule to all currently
 * uncategorized transactions.
 */

import { useMemo, useState } from 'react';
import {
  useFinanceRules,
  useFinanceCategories,
  useFinanceAccounts,
  useCreateFinanceRule,
  useUpdateFinanceRule,
  useArchiveFinanceRule,
  useRunFinanceRules,
  type FinanceRuleDto,
  type FinanceCategoryDto,
  type FinanceAccountDto,
} from '../../api/finance';
import {
  FINANCE_RULE_MATCH_FIELDS,
  FINANCE_RULE_MATCH_TYPES,
  ruleCriteriaCount,
  ruleActionCount,
  type FinanceRuleMatchField,
  type FinanceRuleMatchType,
  type FinanceRuleCriteria,
  type FinanceRuleAction,
} from '@k-os/core';
import { FormModal } from '../../components/FormModal';
import { ConfirmDialog } from '../../components/finance/ConfirmDialog';
import { CategoryPicker } from '../../components/finance/CategoryPicker';
import { accountMoveTargets, financeErrorMessage } from './finance-format';
import formStyles from '../../components/FormModal.module.css';
import styles from './finance.module.css';

const MATCH_FIELD_LABELS: Record<FinanceRuleMatchField, string> = {
  description: 'Description',
  bank_category: 'Bank category',
  bank_type: 'Bank type',
};

const MATCH_TYPE_LABELS: Record<FinanceRuleMatchType, string> = {
  contains: 'contains',
  equals: 'is exactly',
};

export function FinanceRulesScreen() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const rulesQ = useFinanceRules({ includeArchived });
  const rules = rulesQ.data ?? [];

  // Include inactive so a rule pointing at a deactivated category still resolves
  // to a name in the table.
  const categoriesQ = useFinanceCategories({ includeInactive: true });
  const categories = useMemo(() => categoriesQ.data ?? [], [categoriesQ.data]);
  const categoryName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  // Include archived so a rule referencing a deactivated account still resolves
  // to a name in the table; also feeds the editor's account/move selects.
  const accountsQ = useFinanceAccounts({ includeArchived: true });
  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);
  const accountName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts) m.set(a.id, a.name);
    return m;
  }, [accounts]);

  const run = useRunFinanceRules();
  const [ranResult, setRanResult] = useState<
    { categorized: number; moved: number; moveErrors: number } | null
  >(null);

  // Modal / dialog state.
  const [form, setForm] = useState<
    { mode: 'create' } | { mode: 'edit'; rule: FinanceRuleDto } | null
  >(null);
  const [deleting, setDeleting] = useState<FinanceRuleDto | null>(null);

  const runNow = () => {
    if (run.isPending) return;
    setRanResult(null);
    run.mutate(undefined, {
      onSuccess: (res) =>
        setRanResult({ categorized: res.categorized, moved: res.moved, moveErrors: res.moveErrors }),
    });
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Rules</h2>
          <div className={styles.subtitle}>
            Auto-categorization rules run on every import and can be re-applied on
            demand. Lower priority wins; the first matching rule decides the
            category, and a rule is skipped when its category’s kind contradicts the
            transaction’s sign.
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.toggle} ${includeArchived ? styles.toggleOn : ''}`}
            onClick={() => setIncludeArchived((v) => !v)}
          >
            {includeArchived ? 'Showing archived' : 'Show archived'}
          </button>
          <button
            type="button"
            className="kos-btn"
            disabled={run.isPending}
            onClick={runNow}
          >
            {run.isPending ? 'Running…' : 'Run rules now'}
          </button>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            onClick={() => setForm({ mode: 'create' })}
          >
            + New rule
          </button>
        </div>
      </header>

      {ranResult !== null && (
        <div className={styles.fieldHint}>
          Categorized {ranResult.categorized} transaction{ranResult.categorized === 1 ? '' : 's'}
          {ranResult.moved > 0 ? ` · moved ${ranResult.moved}` : ''}
          {ranResult.moveErrors > 0
            ? ` · ${ranResult.moveErrors} move${ranResult.moveErrors === 1 ? '' : 's'} skipped`
            : ''}
          .
        </div>
      )}
      {run.isError && <div className={styles.error}>{financeErrorMessage(run.error)}</div>}

      {rulesQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : rulesQ.isError ? (
        <div className={styles.error}>{financeErrorMessage(rulesQ.error)}</div>
      ) : rules.length === 0 ? (
        <div className={styles.empty}>
          No rules yet. Create one here, or use “Always do this” after categorizing a
          transaction.
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 72, textAlign: 'right' }}>Priority</th>
                <th>When</th>
                <th>Then</th>
                <th style={{ width: 96 }}>Status</th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => {
                const archived = rule.archivedAt !== null;
                return (
                  <tr key={rule.id} className={archived || !rule.active ? styles.inactive : undefined}>
                    <td className={styles.num} style={{ textAlign: 'right' }}>
                      {rule.priority}
                    </td>
                    <td>
                      <RuleCriteriaSummary criteria={rule.criteria} accountName={accountName} />
                    </td>
                    <td>
                      <RuleActionSummary
                        action={rule.action}
                        categoryName={categoryName}
                        accountName={accountName}
                      />
                    </td>
                    <td>
                      {archived ? (
                        <span className={styles.badge}>archived</span>
                      ) : (
                        <ActiveToggle rule={rule} />
                      )}
                    </td>
                    <td>
                      {!archived && (
                        <div className={styles.treeActions}>
                          <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={() => setForm({ mode: 'edit', rule })}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                            onClick={() => setDeleting(rule)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <RuleFormModal
        form={form}
        categories={categories}
        accounts={accounts}
        onClose={() => setForm(null)}
      />
      <DeleteRuleDialog rule={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}

// ─── Active toggle ───────────────────────────────────────────────────────────

function ActiveToggle({ rule }: { rule: FinanceRuleDto }) {
  const update = useUpdateFinanceRule();
  return (
    <button
      type="button"
      className={`${styles.toggle} ${rule.active ? styles.toggleOn : ''}`}
      disabled={update.isPending}
      onClick={() => update.mutate({ id: rule.id, patch: { active: !rule.active } })}
      title={rule.active ? 'Active — click to pause' : 'Paused — click to activate'}
    >
      {rule.active ? 'Active' : 'Paused'}
    </button>
  );
}

// ─── Table summaries ──────────────────────────────────────────────────────────

function RuleCriteriaSummary({
  criteria,
  accountName,
}: {
  criteria: FinanceRuleCriteria;
  accountName: Map<string, string>;
}) {
  const parts: React.ReactNode[] = [];

  for (const c of criteria.conditions) {
    parts.push(
      <span key={`cond-${parts.length}`}>
        <span className={styles.ruleField}>{MATCH_FIELD_LABELS[c.field]}</span>{' '}
        {MATCH_TYPE_LABELS[c.type]}{' '}
        <span className={styles.rulePattern}>“{c.pattern}”</span>
      </span>,
    );
  }

  if (criteria.amountMin != null || criteria.amountMax != null) {
    const min = criteria.amountMin != null ? String(criteria.amountMin) : '−∞';
    const max = criteria.amountMax != null ? String(criteria.amountMax) : '+∞';
    parts.push(
      <span key={`amount-${parts.length}`}>
        amount <span className={styles.rulePattern}>[{min}..{max}]</span>
      </span>,
    );
  }

  if (criteria.accountId) {
    parts.push(
      <span key={`account-${parts.length}`}>
        account{' '}
        <span className={styles.rulePattern}>
          {accountName.get(criteria.accountId) ?? 'unknown account'}
        </span>
      </span>,
    );
  }

  if (parts.length === 0) return <span className={styles.muted}>any transaction</span>;

  return (
    <span>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && <span className={styles.muted}> · </span>}
          {p}
        </span>
      ))}
    </span>
  );
}

function RuleActionSummary({
  action,
  categoryName,
  accountName,
}: {
  action: FinanceRuleAction;
  categoryName: Map<string, string>;
  accountName: Map<string, string>;
}) {
  const parts: React.ReactNode[] = [];

  if (action.categoryId) {
    parts.push(
      <span key="cat">
        → {categoryName.get(action.categoryId) ?? <span className={styles.muted}>unknown category</span>}
      </span>,
    );
  }

  if (action.move) {
    parts.push(
      <span key="move">
        move{' '}
        <span className={styles.rulePattern}>
          {accountName.get(action.move.originAccountId) ?? 'unknown'}→
          {accountName.get(action.move.destinationAccountId) ?? 'unknown'}
        </span>
      </span>,
    );
  }

  if (parts.length === 0) return <span className={styles.muted}>no action</span>;

  return (
    <span>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && <span className={styles.muted}> · </span>}
          {p}
        </span>
      ))}
    </span>
  );
}

// ─── Create / edit modal ───────────────────────────────────────────────────────

type RuleForm = { mode: 'create' } | { mode: 'edit'; rule: FinanceRuleDto };

type ConditionState = { field: FinanceRuleMatchField; type: FinanceRuleMatchType; pattern: string };

function RuleFormModal({
  form,
  categories,
  accounts,
  onClose,
}: {
  form: RuleForm | null;
  categories: FinanceCategoryDto[];
  accounts: FinanceAccountDto[];
  onClose: () => void;
}) {
  const create = useCreateFinanceRule();
  const update = useUpdateFinanceRule();

  const open = !!form;
  const [conditions, setConditions] = useState<ConditionState[]>([
    { field: 'description', type: 'contains', pattern: '' },
  ]);
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [accountId, setAccountId] = useState(''); // '' = Any
  const [categoryId, setCategoryId] = useState(''); // '' = no category action
  const [originAccountId, setOriginAccountId] = useState('');
  const [destinationAccountId, setDestinationAccountId] = useState('');
  const [priority, setPriority] = useState('100');
  const [active, setActive] = useState(true);
  const [applyNow, setApplyNow] = useState(true);
  const [seen, setSeen] = useState<string | null>(null);

  const key = open ? (form.mode === 'edit' ? `edit:${form.rule.id}` : 'create') : null;
  if (open && key !== seen) {
    setSeen(key);
    if (form.mode === 'edit') {
      const c = form.rule.criteria;
      setConditions(
        c.conditions.length > 0
          ? c.conditions.map((cond) => ({ ...cond }))
          : [{ field: 'description', type: 'contains', pattern: '' }],
      );
      setAmountMin(c.amountMin != null ? String(c.amountMin) : '');
      setAmountMax(c.amountMax != null ? String(c.amountMax) : '');
      setAccountId(c.accountId ?? '');
      const a = form.rule.action;
      setCategoryId(a.categoryId ?? '');
      setOriginAccountId(a.move?.originAccountId ?? '');
      setDestinationAccountId(a.move?.destinationAccountId ?? '');
      setPriority(String(form.rule.priority));
      setActive(form.rule.active);
    } else {
      setConditions([{ field: 'description', type: 'contains', pattern: '' }]);
      setAmountMin('');
      setAmountMax('');
      setAccountId('');
      setCategoryId('');
      setOriginAccountId('');
      setDestinationAccountId('');
      setPriority('100');
      setActive(true);
      setApplyNow(true);
    }
    create.reset();
    update.reset();
  }
  if (!open && seen !== null) setSeen(null);

  // Linked accounts only (a sub-account, or a parent of one) may be a move
  // origin. When the criteria account is itself linked, narrow to it.
  const originOptions = useMemo(() => {
    const linked = accounts.filter(
      (a) => a.parentAccountId || accounts.some((x) => x.parentAccountId === a.id),
    );
    if (accountId && linked.some((a) => a.id === accountId)) {
      return linked.filter((a) => a.id === accountId);
    }
    return linked;
  }, [accounts, accountId]);

  const destinationOptions = useMemo(
    () => (originAccountId ? accountMoveTargets(accounts, originAccountId) : []),
    [accounts, originAccountId],
  );

  const pending = create.isPending || update.isPending;
  const priorityNum = Number(priority);
  const priorityValid = Number.isInteger(priorityNum) && priorityNum >= 1 && priorityNum <= 9999;

  const criteria: FinanceRuleCriteria = {
    conditions: conditions
      .filter((c) => c.pattern.trim())
      .map((c) => ({ field: c.field, type: c.type, pattern: c.pattern.trim() })),
    ...(amountMin !== '' && Number.isFinite(Number(amountMin)) ? { amountMin: Number(amountMin) } : {}),
    ...(amountMax !== '' && Number.isFinite(Number(amountMax)) ? { amountMax: Number(amountMax) } : {}),
    ...(accountId ? { accountId } : {}),
  };
  const action: FinanceRuleAction = {
    ...(categoryId ? { categoryId } : {}),
    ...(originAccountId && destinationAccountId
      ? { move: { originAccountId, destinationAccountId } }
      : {}),
  };

  const moveBalanced = !!originAccountId === !!destinationAccountId;
  const valid =
    priorityValid &&
    ruleCriteriaCount(criteria) >= 1 &&
    ruleActionCount(action) >= 1 &&
    moveBalanced;
  const error = financeErrorMessage(create.error ?? update.error);

  const setCondition = (i: number, patch: Partial<ConditionState>) =>
    setConditions((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addCondition = () =>
    setConditions((cs) => [...cs, { field: 'description', type: 'contains', pattern: '' }]);
  const removeCondition = (i: number) =>
    setConditions((cs) => cs.filter((_, idx) => idx !== i));

  const submit = () => {
    if (!form || !valid || pending) return;
    if (form.mode === 'edit') {
      update.mutate(
        { id: form.rule.id, patch: { criteria, action, priority: priorityNum, active } },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(
        { criteria, action, priority: priorityNum, active, applyNow },
        { onSuccess: onClose },
      );
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={form?.mode === 'edit' ? 'Edit rule' : 'New rule'}
      subtitle="Lower priority wins; the first matching rule applies. A rule needs at least one criterion and one action."
      submitLabel={pending ? 'Saving…' : form?.mode === 'edit' ? 'Save changes' : 'Create rule'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
    >
      {/* ── Criteria ── */}
      <div className={formStyles.field}>
        <span className={formStyles.fieldLabel}>Criteria</span>
        {conditions.map((c, i) => (
          <div key={i} className={formStyles.row} style={{ marginBottom: 8 }}>
            <select
              className={formStyles.input}
              aria-label="Match field"
              value={c.field}
              onChange={(e) => setCondition(i, { field: e.target.value as FinanceRuleMatchField })}
            >
              {FINANCE_RULE_MATCH_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {MATCH_FIELD_LABELS[f]}
                </option>
              ))}
            </select>
            <select
              className={formStyles.input}
              aria-label="Comparison"
              value={c.type}
              onChange={(e) => setCondition(i, { type: e.target.value as FinanceRuleMatchType })}
            >
              {FINANCE_RULE_MATCH_TYPES.map((t) => (
                <option key={t} value={t}>
                  {MATCH_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <input
              className={formStyles.input}
              aria-label="Pattern"
              value={c.pattern}
              maxLength={200}
              onChange={(e) => setCondition(i, { pattern: e.target.value })}
              placeholder="e.g. CONTINENTE"
            />
            {conditions.length > 1 && (
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                aria-label="Remove condition"
                onClick={() => removeCondition(i)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <button type="button" className={styles.iconBtn} onClick={addCondition}>
          + Add condition
        </button>
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={formStyles.fieldLabel} htmlFor="rule-amount-min">
            Amount min
          </label>
          <input
            id="rule-amount-min"
            className={formStyles.input}
            inputMode="decimal"
            value={amountMin}
            onChange={(e) => setAmountMin(e.target.value)}
            placeholder="Min €"
          />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.fieldLabel} htmlFor="rule-amount-max">
            Amount max
          </label>
          <input
            id="rule-amount-max"
            className={formStyles.input}
            inputMode="decimal"
            value={amountMax}
            onChange={(e) => setAmountMax(e.target.value)}
            placeholder="Max €"
          />
        </div>
      </div>
      <span className={formStyles.fieldHint}>Signed EUR. Max 0 → expenses; Min 0 → income.</span>

      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="rule-account">
          Account
        </label>
        <select
          id="rule-account"
          className={formStyles.input}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">Any account</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      {/* ── Actions ── */}
      <div className={formStyles.field}>
        <span className={formStyles.fieldLabel}>Assign category</span>
        <CategoryPicker
          categories={categories}
          value={categoryId || null}
          placeholder="No category"
          ariaLabel="Assign category"
          onChange={(catId) => setCategoryId(catId)}
        />
        {categoryId && (
          <button
            type="button"
            className={styles.iconBtn}
            style={{ alignSelf: 'flex-start', marginTop: 4 }}
            onClick={() => setCategoryId('')}
          >
            Clear category
          </button>
        )}
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={formStyles.fieldLabel} htmlFor="rule-move-origin">
            Move from
          </label>
          <select
            id="rule-move-origin"
            className={formStyles.input}
            value={originAccountId}
            onChange={(e) => {
              setOriginAccountId(e.target.value);
              setDestinationAccountId('');
            }}
          >
            <option value="">— none —</option>
            {originOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.fieldLabel} htmlFor="rule-move-dest">
            Move to
          </label>
          <select
            id="rule-move-dest"
            className={formStyles.input}
            value={destinationAccountId}
            disabled={!originAccountId}
            onChange={(e) => setDestinationAccountId(e.target.value)}
          >
            <option value="">— none —</option>
            {destinationOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <span className={formStyles.fieldHint}>
        Set both Move from and Move to (linked accounts only), or leave both blank.
      </span>

      {/* ── Meta ── */}
      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={formStyles.fieldLabel} htmlFor="rule-priority">
            Priority
          </label>
          <input
            id="rule-priority"
            className={formStyles.input}
            type="number"
            min={1}
            max={9999}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
          <span className={formStyles.fieldHint}>1–9999; lower runs first.</span>
        </div>
        <div className={formStyles.field}>
          <span className={formStyles.fieldLabel}>Status</span>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active (runs on import)
          </label>
        </div>
      </div>

      {form?.mode === 'create' && (
        <div className={formStyles.field}>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={applyNow}
              onChange={(e) => setApplyNow(e.target.checked)}
            />
            Apply to existing uncategorized transactions now
          </label>
        </div>
      )}
    </FormModal>
  );
}

// ─── Delete confirm ────────────────────────────────────────────────────────────

function DeleteRuleDialog({
  rule,
  onClose,
}: {
  rule: FinanceRuleDto | null;
  onClose: () => void;
}) {
  const archive = useArchiveFinanceRule();
  const open = !!rule;

  const [seen, setSeen] = useState<string | null>(null);
  if (open && rule.id !== seen) {
    setSeen(rule.id);
    archive.reset();
  }
  if (!open && seen !== null) setSeen(null);

  return (
    <ConfirmDialog
      open={open}
      title="Delete rule"
      message={
        rule
          ? 'Delete this rule? Already-categorized transactions keep their category; the rule just stops running on future imports.'
          : ''
      }
      confirmLabel={archive.isPending ? 'Deleting…' : 'Delete'}
      confirmDisabled={archive.isPending}
      destructive
      error={financeErrorMessage(archive.error)}
      onClose={onClose}
      onConfirm={() => {
        if (!rule) return;
        archive.mutate(rule.id, { onSuccess: onClose });
      }}
    />
  );
}
